import React, { useState, useEffect, useCallback } from 'react';
import { api, useLanguage } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, Target, Compass, Loader2, Zap, 
  Trophy, Clock, CheckCircle, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORY_ICONS = {
  constancy: Shield,
  performance: Target,
  engagement: Compass,
};

const AdaptiveChallengeCard = ({ compact = false }) => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const fetchChallenge = useCallback(async () => {
    try {
      const res = await api.get('/challenges/adaptive/current');
      setChallenge(res.data.challenge);
      if (res.data.challenge?.status === 'completed' && !showCelebration) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 5000);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [showCelebration]);

  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/challenges/adaptive/generate');
      if (res.data.success) {
        setChallenge(res.data.challenge);
        toast.success(fr ? 'Mission assignée!' : 'Mission assigned!');
      } else {
        toast.error(res.data.message || 'Erreur');
      }
    } catch {
      toast.error(fr ? 'Erreur de génération' : 'Generation error');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return compact ? null : (
      <Card className="bg-card border-border">
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // No active challenge — show generate button
  if (!challenge || challenge.status === 'expired') {
    if (compact) return null;
    return (
      <Card className="bg-card border-border border-dashed" data-testid="adaptive-no-challenge">
        <CardContent className="py-6 text-center space-y-3">
          <AlertTriangle className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">
            {fr ? 'Aucune mission active. Prêt pour un défi tactique ?' : 'No active mission. Ready for a tactical challenge?'}
          </p>
          <Button 
            onClick={handleGenerate} 
            disabled={generating}
            data-testid="generate-challenge-btn"
            className="bg-primary text-primary-foreground font-heading uppercase"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
            {fr ? 'Lancer une mission' : 'Launch a mission'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const Icon = CATEGORY_ICONS[challenge.category] || Shield;
  const progressPct = Math.min(100, Math.round((challenge.progress / challenge.target) * 100));
  const isCompleted = challenge.status === 'completed';
  const codeName = fr ? challenge.code_name_fr : challenge.code_name_en;
  const objective = fr ? challenge.objective_fr : challenge.objective_en;

  // Calculate time remaining
  let timeLeft = '';
  if (challenge.expires_at && !isCompleted) {
    const expiresAt = new Date(challenge.expires_at);
    const now = new Date();
    const diffMs = expiresAt - now;
    if (diffMs > 0) {
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      timeLeft = days > 0 ? `${days}j ${hours}h` : `${hours}h`;
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Card 
          className={`relative overflow-hidden border ${isCompleted ? 'border-green-500/50 bg-green-500/5' : 'border-border'}`}
          style={!isCompleted ? { borderColor: `${challenge.color}40`, background: `linear-gradient(135deg, ${challenge.color}08, transparent)` } : {}}
          data-testid="adaptive-challenge-card"
        >
          {/* x2 Badge */}
          <div className="absolute top-2 right-2">
            <span 
              className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest rounded"
              style={{ backgroundColor: `${challenge.color}25`, color: challenge.color, border: `1px solid ${challenge.color}50` }}
              data-testid="reward-multiplier"
            >
              x{challenge.reward_multiplier} PTS
            </span>
          </div>

          <CardContent className={compact ? 'py-3 px-4' : 'pt-5 pb-4'}>
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${challenge.color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: challenge.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-xs uppercase tracking-widest text-muted-foreground">
                  {fr ? 'Mission Active' : 'Active Mission'}
                </p>
                <h3 className="font-heading text-base uppercase tracking-tight text-foreground" data-testid="challenge-name">
                  {codeName}
                </h3>
              </div>
            </div>

            {/* Objective */}
            {!compact && (
              <p className="text-sm text-muted-foreground mb-3" data-testid="challenge-objective">
                {objective}
              </p>
            )}

            {/* Progress Bar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {challenge.progress}/{challenge.target}
                </span>
                <span className="text-xs font-bold" style={{ color: isCompleted ? '#22c55e' : challenge.color }}>
                  {progressPct}%
                </span>
              </div>
              <div className="w-full h-2.5 bg-secondary rounded-full overflow-hidden">
                <motion.div 
                  className="h-full rounded-full"
                  style={{ backgroundColor: isCompleted ? '#22c55e' : challenge.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-3">
              {isCompleted ? (
                <div className="flex items-center gap-2 text-green-500" data-testid="challenge-completed">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs font-heading uppercase">
                    {fr ? 'Mission accomplie!' : 'Mission complete!'}
                  </span>
                  <span className="text-xs font-bold">+{challenge.points_awarded} pts</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  <span className="text-[10px]">{timeLeft}</span>
                </div>
              )}

              {isCompleted && !compact && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="next-mission-btn"
                  className="text-xs"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${generating ? 'animate-spin' : ''}`} />
                  {fr ? 'Prochaine mission' : 'Next mission'}
                </Button>
              )}
            </div>
          </CardContent>

          {/* Celebration overlay */}
          {showCelebration && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-green-500/10 backdrop-blur-sm flex items-center justify-center"
            >
              <div className="text-center">
                <Trophy className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                <p className="font-heading text-lg text-foreground uppercase">
                  {fr ? 'Mission Accomplie!' : 'Mission Complete!'}
                </p>
                <p className="text-sm text-green-400 font-bold">+{challenge.points_awarded} pts (x{challenge.reward_multiplier})</p>
              </div>
            </motion.div>
          )}
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdaptiveChallengeCard;
